import { zip } from './zip.ts';

/**
 * Writing an Excel workbook, by hand.
 *
 * The mirror of `xlsx.ts` next door, which reads one. Same reasoning: an
 * `.xlsx` is a ZIP of XML files, `zip.ts` already writes the ZIP, and every
 * dependency is a supply-chain surface in an app that holds clinical records.
 * A spreadsheet library is a large thing to take on for what is, underneath,
 * a few kilobytes of XML in a shape that has not changed since 2007.
 *
 * The scope is deliberately narrow, the same way the reader's is: cell text,
 * numbers, a bold frozen header row and readable column widths. No formulas,
 * no charts, no merged cells, no themes. Everything happens in the browser —
 * the workbook is built and downloaded locally and never uploaded.
 */

export type Sheet = { name: string; rows: unknown[][] };

/**
 * XML 1.0 permits tab, newline and carriage return, and nothing else below
 * 0x20. A single stray control character makes the whole workbook unopenable
 * rather than merely odd-looking, and `note` is free text a trainee may have
 * pasted in from anywhere, so they are dropped rather than escaped.
 *
 * The lint rule against control characters in a regex is disabled here on
 * purpose: matching them is the entire job of this expression.
 */
// eslint-disable-next-line no-control-regex
const FORBIDDEN = /[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g;

function escapeXml(text: string): string {
  return text
    .replace(FORBIDDEN, '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** 0 → A, 25 → Z, 26 → AA. Excel's own column naming: base 26 with no zero. */
export function columnLetter(index: number): string {
  let remaining = index + 1;
  let letters = '';

  while (remaining > 0) {
    const rem = (remaining - 1) % 26;
    letters = String.fromCharCode(65 + rem) + letters;
    remaining = Math.floor((remaining - 1) / 26);
  }

  return letters;
}

/**
 * Excel refuses a workbook whose sheet name is empty, over 31 characters, or
 * contains any of `: \ / ? * [ ]`. Every name this app passes is already
 * safe; this is here so that stays true if someone adds a sheet later.
 */
export function safeSheetName(name: string, fallback: string): string {
  const cleaned = name
    .replace(/[:\\/?*[\]]/g, ' ')
    .trim()
    .slice(0, 31);

  return cleaned === '' ? fallback : cleaned;
}

function cellXml(value: unknown, ref: string, isHeader: boolean): string {
  const style = isHeader ? ' s="1"' : '';

  /*
   * Numbers are written as numbers, not text, so a capability level or an
   * attempt count sorts and sums in Excel rather than ordering "10" before
   * "2" — much of the point of offering a spreadsheet over a CSV.
   */
  if (typeof value === 'number' && Number.isFinite(value)) {
    return `<c r="${ref}"${style}><v>${String(value)}</v></c>`;
  }

  const text = value === undefined || value === null ? '' : String(value);
  if (text === '') return `<c r="${ref}"${style}/>`;

  /*
   * Inline strings rather than a shared string table. A table would save
   * bytes where text repeats, but it is a second part to keep consistent
   * with the sheets, and an export opened once in Excel does not need the
   * kilobytes back.
   */
  return `<c r="${ref}"${style} t="inlineStr"><is><t xml:space="preserve">${escapeXml(text)}</t></is></c>`;
}

/**
 * Column widths are computed from the content rather than left to Excel,
 * which defaults every column to the same width and leaves a trainee
 * dragging borders before they can read their own notes. Measured in Excel's
 * "characters of the default font" unit, clamped so one long note does not
 * produce a single column wider than the screen.
 */
function columnsXml(rows: readonly unknown[][]): string {
  const widths: number[] = [];

  for (const row of rows) {
    row.forEach((value, index) => {
      const length = value === undefined || value === null ? 0 : String(value).length;
      widths[index] = Math.max(widths[index] ?? 0, length);
    });
  }

  if (widths.length === 0) return '';

  const cols = widths
    .map((width, index) => {
      const clamped = Math.min(Math.max(width + 2, 8), 40);
      const at = String(index + 1);
      return `<col min="${at}" max="${at}" width="${String(clamped)}" customWidth="1"/>`;
    })
    .join('');

  return `<cols>${cols}</cols>`;
}

function sheetXml(rows: readonly unknown[][]): string {
  const rowsXml = rows
    .map((row, rowIndex) => {
      const cells = row
        .map((value, colIndex) =>
          cellXml(value, `${columnLetter(colIndex)}${String(rowIndex + 1)}`, rowIndex === 0),
        )
        .join('');

      return `<row r="${String(rowIndex + 1)}">${cells}</row>`;
    })
    .join('');

  /*
   * The header row is frozen. A logbook export runs to hundreds of rows and
   * the columns are not self-evident from the values alone — "3" means
   * nothing once "capability level" has scrolled off the top.
   */
  const freeze =
    '<sheetViews><sheetView workbookViewId="0">' +
    '<pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/>' +
    '</sheetView></sheetViews>';

  return (
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
    freeze +
    columnsXml(rows) +
    `<sheetData>${rowsXml}</sheetData>` +
    '</worksheet>'
  );
}

/**
 * Two cell formats: the default, and a bold one for the header row. Excel
 * will open a workbook with no styles part at all, but then the header is
 * indistinguishable from the data, which matters more here than it sounds —
 * these sheets get read by a supervisor, not only by the trainee.
 */
const STYLES_XML =
  '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
  '<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
  '<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font>' +
  '<font><b/><sz val="11"/><name val="Calibri"/></font></fonts>' +
  '<fills count="1"><fill><patternFill patternType="none"/></fill></fills>' +
  '<borders count="1"><border/></borders>' +
  '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>' +
  '<cellXfs count="2">' +
  '<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>' +
  '<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>' +
  '</cellXfs>' +
  '</styleSheet>';

/** The parts of a workbook, by name — the whole package, ready to zip. */
export function xlsxParts(sheets: readonly Sheet[]): Record<string, string> {
  const named = sheets.map((sheet, index) => ({
    ...sheet,
    name: safeSheetName(sheet.name, `Sheet${String(index + 1)}`),
  }));

  const files: Record<string, string> = {};

  /*
   * `[Content_Types].xml` must declare every part in the package. Excel
   * reports a workbook as corrupt — with no useful detail — if one is
   * missing, which is the single easiest way to get this format wrong.
   */
  files['[Content_Types].xml'] =
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">' +
    '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
    '<Default Extension="xml" ContentType="application/xml"/>' +
    '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>' +
    '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>' +
    named
      .map(
        (_, index) =>
          `<Override PartName="/xl/worksheets/sheet${String(index + 1)}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`,
      )
      .join('') +
    '</Types>';

  files['_rels/.rels'] =
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
    '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>' +
    '</Relationships>';

  files['xl/workbook.xml'] =
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" ' +
    'xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets>' +
    named
      .map(
        (sheet, index) =>
          `<sheet name="${escapeXml(sheet.name)}" sheetId="${String(index + 1)}" r:id="rId${String(index + 1)}"/>`,
      )
      .join('') +
    '</sheets></workbook>';

  // The styles part takes the id after the last sheet, so the numbering holds
  // however many sheets there are.
  const stylesId = named.length + 1;

  files['xl/_rels/workbook.xml.rels'] =
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
    named
      .map(
        (_, index) =>
          `<Relationship Id="rId${String(index + 1)}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${String(index + 1)}.xml"/>`,
      )
      .join('') +
    `<Relationship Id="rId${String(stylesId)}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>` +
    '</Relationships>';

  files['xl/styles.xml'] = STYLES_XML;

  named.forEach((sheet, index) => {
    files[`xl/worksheets/sheet${String(index + 1)}.xml`] = sheetXml(sheet.rows);
  });

  return files;
}

/** A complete `.xlsx`, ready to hand to a download. */
export function xlsx(sheets: readonly Sheet[]): Blob {
  return new Blob([zip(xlsxParts(sheets))], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}
